import { Lightning, Log, Registry, Utils } from '@lightningjs/sdk';
import { Theme } from '../../config/theme';
import { CmsConstants } from '../../Business/cms/CmsConstants';
import { CmsCardDummy } from './CmsCardDummy';
import { RibbonTranslationKeys } from '../../api/models/TranslationKeys';

const _constants = {
  gradientWidth: 180,
};

export class CmsRibbon extends Lightning.Component {
  static _template() {
    return {
      x: 0,
      y: 0,
      w: w => w,
      h: Theme.Fonts.Ribbon.Title.lineHeight,
      TitleContainer: {
        y: 0,
        h: Theme.Fonts.Ribbon.Title.lineHeight,
        w: w => w,
        flex: {
          direction: 'row',
        },
        Title: {
          flexItem: {},
          y: 0,
          h: Theme.Fonts.Ribbon.Title.lineHeight,
          text: {
            ...Theme.Fonts.Ribbon.Title,
          },
          color: Theme.Colors.Ribbon.Title,
        },
        FavoriteIcon: {
          mountY: 0.5,
          y: Theme.Fonts.Ribbon.Title.lineHeight / 2,
          flexItem: {
            marginLeft: 20,
          },
          visible: false,
          w: Theme.Fonts.Ribbon.Title.iconSize,
          h: Theme.Fonts.Ribbon.Title.iconSize,
          src: Utils.asset('images/guide/ic_star.png'),
          color: Theme.Colors.Primary,
        },
      },
      Cards: {
        color: 0xff800080,
      },
      Background: {
        w: w => w,
        h: h => h,
        NoContent: {
          mount: 0.5,
          x: w => w / 2,
          y: h => h / 2,
          visible: false,
          text: {
            ...Theme.Fonts.Ribbon.Title,
            text: '',
          },
        },
      },
      LeftGradient: {
        w: _constants.gradientWidth,
        y: Theme.Fonts.Ribbon.Title.lineHeight + CmsConstants.Card.borderSize,
        h: h => h - Theme.Fonts.Ribbon.Title.lineHeight - CmsConstants.Card.borderSize * 2,
        rect: true,
        colorLeft: Theme.Colors.Black,
        colorRight: Theme.Colors.Transparent,
        alpha: 0,
      },
    };
  }

  _init() {
    this.focusIndex = -1;
    if (this.fadeOut === undefined) this.fadeOut = true;
    this.alpha = this.fadeOut ? CmsConstants.Animation.fadeOut : 1;
    this.cardXCount = 0;

    if (!this.contentOffset) this.contentOffset = 0;

    this.dummies = [];
    this.tag('Cards').h = this.data.sizeInfo.cardHeight;
    this.tag('Cards').x = this.contentOffset;

    this.updateTitle();
    this.addItems();
  }

  reloadContent() {
    this.updateTitle();
    this.addItems();
  }

  updateTitle() {
    const titleContainer = this.tag('TitleContainer');
    const items = this.tag('Cards');
    if (this.data.favorite) {
      this.data.favorite.then(result => {
        this.isFavorite = result;
        if (result) this.tag('FavoriteIcon').visible = true;
      });
    }
    if (this.data.title) {
      const offset = this.contentOffset + CmsConstants.Ribbon.titleOffset;
      titleContainer.patch({
        visible: true,
        x: offset,
        w: w => w - offset,
        Title: {
          text: {
            text: this.data.title,
          },
        },
      });
      items.y = Theme.Fonts.Ribbon.Title.lineHeight;
    } else {
      titleContainer.visible = false;
      items.y = 0;
      this.tag('LeftGradient').patch({
        y: CmsConstants.Card.borderSize,
        h: h => h - CmsConstants.Card.borderSize * 2,
      });
    }
  }

  addItems() {
    const items = this.tag('Cards');
    this.dummies.forEach(dummie => {
      dummie.startLoading();
    });

    // datasource get new cards with loadNext()
    return this.data
      .loadNext()
      .then(newItems => {
        this.tag('NoContent').visible = false;
        if (this.data.isLoaded === false) {
          return;
        }
        if (!newItems || newItems.length === 0) {
          // all items loaded
          if (items.children.length === 0 && this.data.message) {
            // show message
            this.tag('NoContent').visible = true;
            this.tag('NoContent').text.text = this.data.message;
          } else if (items.children.length === 0) {
            this.tag('NoContent').visible = true;
            this.tag('NoContent').text.text = this.data.backend.translations.forKey(
              RibbonTranslationKeys.ribbon_no_content_ott
            );
          }
          return;
        }

        // add items
        const components = newItems.map(ds => {
          const card = {
            x: this.cardXCount,
            ...ds.componentDescription(),
          };
          this.cardXCount += CmsConstants.Ribbon.margin + ds.sizeInfo.width;
          this.cardsWidth = this.cardXCount;
          return card;
        });
        items.childList.a(components);

        if (this.data.hasNext()) {
          if (this.dummies.length === 0) {
            // add dummies
            this.dummies = [
              items.childList.a({
                type: CmsCardDummy,
                w: newItems[0].sizeInfo.width,
                h: newItems[0].sizeInfo.height,
                x: this.cardXCount,
                signals: {
                  startLazyLoad: this._lazy.bind(this),
                },
              }),
              items.childList.a({
                type: CmsCardDummy,
                w: newItems[0].sizeInfo.width,
                h: newItems[0].sizeInfo.height,
                x: this.cardXCount,
              }),
            ];
          }
          // move dummies
          this.dummies.forEach((dummy, idx) => {
            dummy.visible = false;
            items.childList.setAt(dummy, items.childList.length - 1);
            dummy.x = this.cardXCount + (dummy.w + CmsConstants.Ribbon.margin) * idx;
            dummy.visible = true;
          });
        } else {
          // remove dummies
          this.dummies.forEach(dummy => {
            items.childList.remove(dummy);
          });
          this.dummies = [];
        }
        if (this.focusIndex === -1) {
          this.focusIndex = 0;
          this._refocus();
        }
      })
      .catch(err => {
        Log.error(`Error getting new ribbon cards: ${err}`);
        // stop spinners
        this.dummies.forEach(dummie => {
          dummie.stopLoading();
        });
      });
  }

  _lazy() {
    // TODO: remove timeout. Change loadNext to activate after centerCard has happend
    Registry.setTimeout(() => {
      this.addItems();
    });
  }

  _getFocused() {
    if (this.focusIndex !== -1) {
      return this.tag('Cards').children[this.focusIndex];
    }
  }

  _focus() {
    if (this.fadeOut) {
      this.setSmooth('alpha', 1);
    }
    // console.log('RIBBON DATA', this.data);

    this.centerCard();
  }

  _unfocus() {
    if (this.fadeOut) {
      this.setSmooth('alpha', CmsConstants.Animation.fadeOut);
    }
    this.fireAncestors('$hideMiniDetail');
  }

  _handleLeft() {
    if (this.focusIndex > 0) {
      this.focusIndex = this.focusIndex - 1;
      this.centerCard();
      return true;
    }
    return false;
  }

  _handleRight() {
    if (this.focusIndex < this.tag('Cards').children.length - 1 - this.dummies.length) {
      this.focusIndex = this.focusIndex + 1;
      this.centerCard();
      return true;
    }
    return false;
  }

  centerCard() {
    const cards = this.tag('Cards');
    if (this.focusIndex === -1) {
      // center if ribbon is in message mode
      this.signal('centerVertical', this.finalY + cards.finalY + (this.finalH - cards.finalY) * 0.5);
      return;
    }
    let card = this.tag('Cards').children[this.focusIndex];

    const cardCenter = card.finalX + card.finalW * 0.5;
    const sliderWidth = this.finalW;
    const sliderCenter = sliderWidth * 0.5;
    let contentWidth = this.cardsWidth;
    if (this.dummies.length > 0) contentWidth += this.dummies.length * this.dummies[0].w;
    const result = Math.min(this.contentOffset, Math.max(sliderWidth - contentWidth, sliderCenter - cardCenter));
    // console.log('centering card', cardCenter, sliderCenter, result, cards.finalW, this.finalW);

    this.tag('Cards').setSmooth('x', result);
    // if it is close to the left side (10 pixels) activate the gradient
    if (result < 10) {
      this.tag('LeftGradient').setSmooth('alpha', 1);
    } else {
      this.tag('LeftGradient').setSmooth('alpha', 0);
    }
    // console.log('CenterCard', this.finalY, card.finalY, card.finalH);
    // use first child to ensure equal height acros ribbon. for example portrait card with progressbar or without
    this.signal('centerVertical', this.finalY + cards.finalY + (this.finalH - cards.finalY) * 0.5);
  }

  _firstActive() {
    if (this.loadNext) this.signal('startLazyLoad');
  }
}
